import deploymentTimeline from "./humanoid-infographics/humanoid-robot-deployment-timeline.json";
import productionVsDeliveries from "./humanoid-infographics/humanoid-robot-production-vs-deliveries.json";
import failureModes from "./humanoid-infographics/humanoid-robot-failure-modes.json";
import sensorComparison from "./humanoid-infographics/humanoid-robot-sensors-comparison.json";
import handComparison from "./humanoid-infographics/humanoid-robot-hands-comparison.json";
import companyFunding from "./humanoid-infographics/humanoid-robot-company-funding.json";
import hardwareStack from "./humanoid-infographics/humanoid-robot-hardware-stack.json";
import companyDirectory from "./humanoid-company-directory.json";
import { humanoidRobots, humanoidManufacturers } from "./humanoidCatalog";

export const HUMANOID_INTELLIGENCE_UPDATED = "2026-08-17";

export const chinaManufacturerNames = new Set(
  humanoidManufacturers
    .filter((manufacturer) => manufacturer.countryCode === "CN")
    .flatMap((manufacturer) => [manufacturer.name, manufacturer.name.replace(" Robotics", "")]),
);

export const chinaHumanoidModels = humanoidRobots.filter(
  (robot) => robot.robotOriginCountryCode === "CN",
);

export const chinaShipmentLedger = productionVsDeliveries.entries.filter((entry) =>
  chinaManufacturerNames.has(entry.company),
);

export const chinaOrderLedger = productionVsDeliveries.entries.filter(
  (entry) => chinaManufacturerNames.has(entry.company) && Number.isFinite(entry.orders),
);

export const documentedDeployments = deploymentTimeline.entries;
export const documentedFailureEvidence = failureModes.entries;
export const sensorEvidence = sensorComparison.entries;
export const handEvidence = handComparison.entries;
export const fundingEvidence = companyFunding.entries;
export const hardwareEvidence = hardwareStack.entries;

export const chinaComponentSuppliers = companyDirectory.filter(
  (company) => company.countryCode === "CN" && /component/i.test(company.companyType || ""),
);

export const humanoidIntelligenceLinks = [
  {
    path: "/humanoid-robot-company-growth",
    label: "Humanoid Company Growth Tracker",
    shortLabel: "Company growth",
    description: "One cumulative timeline tracking verified humanoid robot companies founded since 2000 by region.",
    indexable: true,
  },
  {
    path: "/humanoid-robots/database",
    label: "Humanoid Robot Database",
    shortLabel: "Robot database",
    description: "Search and filter the verified humanoid catalog by manufacturer, country, status and public price.",
    indexable: true,
  },
  {
    path: "/humanoid-robots/compare",
    label: "Compare Humanoid Robots",
    shortLabel: "Compare robots",
    description: "Compare up to four robots using only fields already backed by the project dataset.",
    indexable: true,
  },
  {
    path: "/china-humanoid-model-tracker",
    label: "China Humanoid Model Tracker",
    shortLabel: "China models",
    description: "Track Chinese humanoid models without mixing prototypes, commercial robots and unrelated platforms.",
    indexable: true,
  },
  {
    path: "/china-humanoid-order-tracker",
    label: "China Humanoid Order Tracker",
    shortLabel: "China orders",
    description: "A strict order ledger. The page remains noindex until the retained project data contains qualifying Chinese commercial order records.",
    indexable: false,
  },
  {
    path: "/china-humanoid-shipment-tracker",
    label: "China Humanoid Shipment Tracker",
    shortLabel: "China shipments",
    description: "Separate production milestones, shipments, deliveries and deployments instead of combining them into one number.",
    indexable: true,
  },
  {
    path: "/robotics/humanoid-robot-company-funding",
    label: "Humanoid Funding Tracker",
    shortLabel: "Funding",
    description: "Existing source-backed funding page reused to avoid creating a duplicate funding URL.",
    indexable: true,
    existing: true,
  },
  {
    path: "/humanoid-robot-deployment-map",
    label: "Humanoid Deployment Map",
    shortLabel: "Deployments",
    description: "Documented customer sites and deployment records, kept separate from headquarters and demos.",
    indexable: true,
  },
  {
    path: "/humanoid-robot-failures",
    label: "Humanoid Robot Failure Database",
    shortLabel: "Failures",
    description: "Source-backed failure evidence without turning isolated clips into reliability claims.",
    indexable: true,
  },
  {
    path: "/china-humanoid-component-supply-chain",
    label: "China Humanoid Component Supply Chain",
    shortLabel: "China components",
    description: "Verified Chinese humanoid component suppliers only. The page stays noindex while coverage is too sparse for a strong standalone database.",
    indexable: false,
  },
];

export const trackerConfigs = {
  "china-models": {
    path: "/china-humanoid-model-tracker",
    title: "China Humanoid Model Tracker",
    eyebrow: "CHINA MODEL DATABASE",
    description: "A source-linked view of Chinese humanoid models already present in the verified TechniaHQRobot catalog. Commercial status and public price remain separate fields.",
    methodology: "This tracker only includes robots whose retained catalog record identifies China as the robot origin country. Missing specifications are shown as not publicly disclosed; they are not estimated.",
    indexable: true,
  },
  "china-orders": {
    path: "/china-humanoid-order-tracker",
    title: "China Humanoid Order Tracker",
    eyebrow: "CHINA ORDER LEDGER",
    description: "A strict commercial-order ledger that does not treat production targets, preorder capacity, demos, shipments or deployments as orders.",
    methodology: "A row qualifies only when the retained project data identifies a Chinese humanoid manufacturer and publishes an order quantity or contract record. No qualifying row is currently retained, so this route is intentionally noindex and excluded from the sitemap until evidence exists.",
    indexable: false,
  },
  "china-shipments": {
    path: "/china-humanoid-shipment-tracker",
    title: "China Humanoid Shipment Tracker",
    eyebrow: "PRODUCTION ≠ SHIPMENTS",
    description: "Track Chinese production, shipments and deliveries without adding incompatible milestones together.",
    methodology: "Rows come from the existing source-backed production-versus-deliveries dataset. Cumulative milestones are displayed independently and never summed when they overlap in time or scope.",
    indexable: true,
  },
  deployments: {
    path: "/humanoid-robot-deployment-map",
    title: "Humanoid Deployment Locations and Agreements",
    eyebrow: "REPORTED LOCATIONS AND MILESTONES",
    description: "Locate reported humanoid projects and agreements while distinguishing operating evidence, repeated site milestones and undisclosed locations.",
    methodology: "This view uses the same records as the deployment timeline. Eleven rows are eleven announcements or milestones, not eleven sites or deployed robots. Manufacturer statements remain attributed, and an agreement does not prove a completed deployment. No robot count is inferred. Sources were reviewed on 29 September 2026.",
    indexable: true,
  },
  failures: {
    path: "/humanoid-robot-failures",
    title: "AgiBot Failure Evidence and Limits",
    eyebrow: "VALIDATION CLAIMS AND DATA CATEGORIES",
    description: "Inspect AgiBot walking-validation statements and five interaction-data categories without treating them as incident counts or fleet reliability rates.",
    methodology: "Two manufacturer publications support this limited collection. Five rows name categories from exploratory teleoperation data, not counted field incidents. The walking page juxtaposes 5,100 hours, up to 1,300 continuous hours and a zero-fall equivalence; their denominators must not be merged. Source review: 29 September 2026.",
    indexable: true,
  },
  components: {
    path: "/china-humanoid-component-supply-chain",
    title: "China Humanoid Component Supply Chain",
    eyebrow: "VERIFIED SUPPLIERS ONLY",
    description: "A strict supplier ledger for Chinese humanoid components. Supplier-to-robot relationships are not inferred.",
    methodology: "Only Chinese companies explicitly classified as humanoid component companies in the retained company directory are eligible. The current verified coverage is too small for indexation, so this route is excluded from the sitemap until the database is expanded.",
    indexable: false,
  },
};
